import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from "react-joyride";

import { selectThemeMode } from "../../../app/slices/GlobalSlice";
import {
  selectActiveStepIndex,
  selectActiveTourId,
  setTourAborted,
  setTourAbandoned,
  setTourCompleted,
  setTourStepIndex,
} from "../../../app/slices/OnboardingSlice";
import {
  onboardingSelector,
  resolveOnboardingTarget,
} from "../onboardingTargets";
import type { OnboardingTour } from "../onboardingTypes";
import { OnboardingTooltip } from "./OnboardingTooltip";

/**
 * The ONLY file in the repo that may import react-joyride.
 *
 * Everything else speaks in OnboardingStepDefinition — an anchor id, a
 * placement and two i18n keys. Swapping the library is therefore a change to
 * this file alone, not to any step content.
 *
 * Enforced by the no-restricted-imports rule in .eslintrc.
 */

export interface TourRunnerProps {
  tour: OnboardingTour;
}

/** Names every target that is not currently in the DOM. */
export function findUnresolvedTargets(tour: OnboardingTour): string[] {
  return tour.steps
    .map((step) => step.target)
    .filter((target) => resolveOnboardingTarget(target) === null);
}

export default function TourRunner({ tour }: TourRunnerProps) {
  const dispatch = useDispatch();
  const activeTourId = useSelector(selectActiveTourId);
  const stepIndex = useSelector(selectActiveStepIndex);
  const themeMode = useSelector(selectThemeMode);
  const progressStartIndex = useSelector(
    (state: any) => tour.progressSegment?.(state)?.startIndex ?? 0,
  );
  const progressLabelKey = useSelector(
    (state: any) => tour.progressSegment?.(state)?.labelKey ?? null,
  );
  const isRunning = activeTourId === tour.id;

  // Guards against a StrictMode double-invoke aborting a healthy tour twice.
  const abortedRef = useRef(false);

  useEffect(() => {
    if (!isRunning) {
      abortedRef.current = false;
      return;
    }
    if (abortedRef.current) return;

    const unresolved = findUnresolvedTargets(tour);
    if (unresolved.length === 0) return;

    abortedRef.current = true;

    if (process.env.NODE_ENV !== "production") {
      // Loud on purpose. A target that fails to resolve while its tour's
      // canRun predicate passes is a real regression, not an empty state.
      // eslint-disable-next-line no-console
      console.error(
        `[onboarding] Tour "${tour.id}" aborted — these anchors did not resolve:\n` +
          unresolved
            .map((t) => `  • ${onboardingSelector(t as any)}`)
            .join("\n") +
          `\n\nThe element is missing or no longer rendered. Fix the anchor, or ` +
          `narrow the tour's canRun predicate so it does not run in this state.`,
      );
    }

    // Persists nothing, so the tour retries once the anchor is fixed.
    dispatch(setTourAborted(tour.id));
  }, [dispatch, isRunning, tour]);

  const steps = useMemo<Step[]>(
    () =>
      tour.steps.map((step) => ({
        target: onboardingSelector(step.target),
        placement: step.placement ?? "bottom",
        disableBeacon: true,
        // Only when the step asks: omitting it leaves joyride's own default.
        ...(step.offset !== undefined ? { offset: step.offset } : {}),
        content: {
          titleKey: step.titleKey,
          bodyKey: step.bodyKey,
          progressStartIndex,
          progressLabelKey,
        } as never,
      })),
    [progressLabelKey, progressStartIndex, tour],
  );

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, index, type } = data;

      if (status === STATUS.FINISHED) {
        dispatch(setTourCompleted(tour.id));
        return;
      }

      // Skip and close are abandonment, never completion — the bug that made
      // the old system burn a tour it had failed to show.
      if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        dispatch(setTourAbandoned({ tourId: tour.id, stepIndex: index }));
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        if (type === EVENTS.TARGET_NOT_FOUND) {
          dispatch(setTourAborted(tour.id));
          return;
        }
        dispatch(setTourStepIndex(index + (action === ACTIONS.PREV ? -1 : 1)));
      }
    },
    [dispatch, tour.id],
  );

  if (!isRunning) return null;

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run
      continuous
      callback={handleCallback}
      tooltipComponent={OnboardingTooltip}
      disableOverlayClose
      disableScrolling
      scrollToFirstStep={false}
      spotlightClicks={false}
      spotlightPadding={2}
      /**
       * The arrow must match the panel it points at. Joyride defaults
       * arrowColor to '#fff', so in dark mode a white arrow hung off a
       * #22262b card.
       *
       * Literal hex rather than var(--pi-surface): react-floater renders the
       * arrow as an SVG <polygon fill={color}>, and var() inside a
       * presentation attribute is not reliably resolved. Keep these two in
       * step with --pi-surface in src/style.css.
       */
      styles={{
        options: {
          zIndex: 2000,
          arrowColor: themeMode === "dark" ? "#22262b" : "#ffffff",
        },
      }}
      /**
       * Keep the panel inside the window, not inside its scroll parent.
       *
       * react-floater passes `options` straight into popper's modifiers, and
       * popper's preventOverflow defaults to boundariesElement "scrollParent"
       * (popper.js/dist/umd/popper.js:2161). The tooltip is portalled onto
       * body, so that boundary is the document rather than the visible window
       * and a tooltip with no room below its target runs off the bottom of the
       * screen instead of being nudged back up.
       *
       * This is a safety net, not the fix: a step should anchor something
       * short enough to leave the tooltip room in the first place. See the
       * note in tours/dashboardTours.ts.
       */
      floaterProps={{
        disableAnimation: true,
        options: {
          preventOverflow: { boundariesElement: "viewport", padding: 8 },
          flip: { boundariesElement: "viewport", padding: 8 },
        },
        /**
         * Brings the arrow and shadow to the mock.
         *
         * Joyride deep-merges its own floaterStyles ON TOP of this object
         * (modules/step.js), so it keeps ownership of arrow.color — which is
         * what we want, since that is the theme-aware value set above. It sets
         * nothing else on the arrow, so length and spread survive.
         *
         * Floater's default arrow is 16 long by 32 wide, roughly twice the
         * beak in the design. length also sets the gap between the card and
         * its anchor, so shrinking it pulls the card 6px closer.
         */
        styles: {
          arrow: { length: 10, spread: 14 },
          /**
           * Floater's default is drop-shadow(0 0 3px rgba(0,0,0,.3)) — tight,
           * dark and centred. The design asks for a softer shadow with some
           * fall. A filter rather than a box-shadow on the card so the arrow
           * is shadowed with it instead of looking cut out.
           */
          floater: {
            filter: "drop-shadow(0 6px 12px rgba(39, 45, 55, 0.18))",
          },
        },
      }}
    />
  );
}
